/*
 * @author: xuemin02
 * @Date: 2021-05-11 15:52:07
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import get from 'lodash/get';
import map from 'lodash/map';
import first from 'lodash/first';
import isEqual from 'lodash/isEqual';
import toLower from 'lodash/toLower';
import reduce from 'lodash/reduce';
import React, { useImperativeHandle, forwardRef, useRef, useCallback, useMemo, useState, useEffect, useLayoutEffect, Fragment } from 'react';
import classnames from 'classnames';
import KeyCode from 'rc-util/lib/KeyCode';
import omit from 'rc-util/lib/omit';
import arrayTreeFilter from 'array-tree-filter';
import OutlinedLeft from 'acud-icon/lib/icons/OutlinedLeft';
import OutlinedRight from 'acud-icon/lib/icons/OutlinedRight';
import OutlinedLoading from 'acud-icon/lib/icons/OutlinedLoading';
import OutlinedDown from 'acud-icon/lib/icons/OutlinedDown';
import Input from '../input';
import Tag from '../tag';
import Tooltip from '../tooltip';
import Clear from '../clear';
import RcCascader from './Cascader';
import renderEmpty from './renderEmpty';
import { ConfigConsumer } from '../config-provider';
import SizeContext from '../config-provider/SizeContext';
import LocaleReceiver, { useLocaleReceiver } from '../locale-provider/LocaleReceiver';
import { replaceElement } from '../_util/reactNode';
import devWarning from '../_util/devWarning';
import { getTransitionName } from '../_util/motion';
import { isEqualArrays, isIncludeArray, isPureType } from './utils';
;
const keepFilteredValueField = '__KEEP_FILTERED_OPTION_VALUE';
// limit the filtered item count by default
const defaultLimit = 50;
const highlightKeyword = (str, keyword, prefixCls) => {
    return str.split(keyword).map((node, index) => (index === 0
        ? node
        : [
            <span className={`${prefixCls}-menu-item-keyword`} key="seperator">
                    {keyword}
                </span>,
            node
        ]));
};
const flattenTree = (options, props, ancestor = []) => {
    const names = getFilledFieldNames(props);
    let flattenOptions = [];
    const childrenName = names.children;
    options.forEach(option => {
        const path = ancestor.concat(option);
        if (props.changeOnSelect || !option[childrenName] || !option[childrenName].length) {
            flattenOptions.push(path);
        }
        if (option[childrenName]) {
            flattenOptions = flattenOptions.concat(flattenTree(option[childrenName], props, path));
        }
    });
    return flattenOptions;
};
const defaultDisplayRender = (label = []) => {
    const labelLength = label.length;
    return reduce(label, (prev, labelItem, labelIndex) => {
        const isLast = labelIndex === labelLength - 1;
        const seperator = isLast ? '' : '>';
        if (isPureType(prev) && isPureType(labelItem)) {
            return prev + labelItem + seperator;
        }
        return <>{prev}{labelItem}{seperator}</>;
    }, '');
};
const defaultFilterOption = (inputValue, path, names) => {
    return path.some(option => {
        if (typeof option[names.label] === 'string') {
            return toLower(option[names.label]).indexOf(toLower(inputValue)) > -1;
        }
        return false;
    });
};
const defaultRenderFilteredOption = (inputValue, path, prefixCls, names) => {
    return path.map((option, index) => {
        const label = option[names.label];
        let node = label;
        if (typeof label === 'string' && label.indexOf(inputValue) > -1) {
            node = highlightKeyword(label, inputValue, prefixCls);
        }
        return index === 0 ? node : ['>', node];
    });
};
const defaultSortFilteredOption = (a, b, inputValue, names) => {
    const callback = (elem) => {
        if (typeof elem[names.label] === 'string') {
            return elem[names.label].indexOf(inputValue) > -1;
        }
        return false;
    };
    return a.findIndex(callback) - b.findIndex(callback);
};
const getFieldNames = ({ fieldNames }) => fieldNames;
const getFilledFieldNames = (props) => {
    const fieldNames = getFieldNames(props) || {};
    const names = {
        children: fieldNames.children || 'children',
        label: fieldNames.label || 'label',
        value: fieldNames.value || 'value'
    };
    return names;
};
const getEmptyNode = (names, notFoundContent) => {
    return {
        [names.value]: 'ACUD_CASCADER_NOT_FOUND',
        [names.label]: notFoundContent,
        disabled: true,
        isEmptyNode: true
    };
};
const Cascader = (props, ref) => {
    const { mode, popupPlacement, locale: cascaderLocale, onClear } = props;
    const [defaultLocale] = useLocaleReceiver('Cascader');
    const locale = Object.assign(Object.assign({}, defaultLocale), cascaderLocale);
    const inputRef = useRef();
    const cachedRef = useRef({});
    const measureRef = useRef();
    const pickerRef = useRef();
    const pickerLabelRef = useRef();
    const tagsRef = useRef();
    const [value, setValue] = useState(props.value || props.defaultValue);
    const [inputValue, setInputValue] = useState('');
    const [inputWidth, setInputWidth] = useState(0);
    const [inputFocused, setInputFocused] = useState(false);
    const [inputCompositionStart, setInputCompositionStart] = useState(false);
    const [popupVisible, setPopupVisible] = useState(props.popupVisible);
    const [pickerLabelOverflow, setPickerLabelOverflow] = useState(false);
    const isMulti = mode === 'multiple';
    const flattenOptions = useMemo(() => {
        const { showSearch, options } = props;
        return showSearch ? flattenTree(options, props) : undefined;
    }, [props]);
    const disabledValue = useMemo(() => {
        const { options } = props;
        const flattenOptions = flattenTree(options, props);
        const disabledOptions = flattenOptions.filter(options => {
            return options.some(option => option.disabled);
        });
        const names = getFilledFieldNames(props);
        return disabledOptions.map(options => options.map(o => o[names.value]));
    }, [props]);
    const focus = useCallback(() => {
        var _a;
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    const blur = useCallback(() => {
        var _a;
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
    }, []);
    const getPopupPlacement = useCallback((direction = 'ltr') => {
        if (popupPlacement !== undefined) {
            return popupPlacement;
        }
        return direction === 'rtl' ? 'bottomRight' : 'bottomLeft';
    }, [popupPlacement]);
    const generateFilteredOptions = useCallback((prefixCls, renderEmpty) => {
        const { showSearch, notFoundContent } = props;
        const names = getFilledFieldNames(props);
        const { filter = defaultFilterOption, render = defaultRenderFilteredOption, sort = defaultSortFilteredOption, limit = defaultLimit } = showSearch;
        let filtered;
        if (limit > 0) {
            filtered = [];
            let matchCount = 0;
            flattenOptions.some(path => {
                const match = filter(inputValue, path, names);
                if (match) {
                    filtered.push(path);
                    matchCount += 1;
                }
                return matchCount >= limit;
            });
        }
        else {
            if (process.env.NODE_ENV !== "production")
                devWarning(typeof limit !== 'number', 'Cascader', "'limit' of showSearch should be positive number or false.");
            filtered = flattenOptions.filter(path => filter(inputValue, path, names));
        }
        filtered = filtered.sort((a, b) => sort(a, b, inputValue, names));
        if (filtered.length > 0) {
            const field = names.value === names.label ? keepFilteredValueField : names.value;
            return filtered.map((path) => ({
                __IS_FILTERED_OPTION: true,
                path,
                [field]: path.map((o) => o[names.value]),
                [names.label]: render(inputValue, path, prefixCls, names),
                disabled: path.some((o) => !!o.disabled),
                isEmptyNode: true
            }));
        }
        return [getEmptyNode(names, notFoundContent || renderEmpty(locale.empty))];
    }, [props, inputValue, flattenOptions, locale.empty]);
    const updateValue = useCallback((value, selectedOptions = []) => {
        var _a;
        if (!('value' in props)) {
            setValue(value);
        }
        (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, value, selectedOptions);
    }, [props]);
    const handleMouseDown = useCallback(e => {
        e.preventDefault();
    }, []);
    const handlePickerLabelHover = useCallback(() => {
        const ele = pickerLabelRef.current;
        const overflow = (ele === null || ele === void 0 ? void 0 : ele.scrollWidth) > (ele === null || ele === void 0 ? void 0 : ele.clientWidth);
        setPickerLabelOverflow(overflow);
    }, []);
    const handleClick = useCallback(e => {
        if (e.target !== inputRef.current) {
            const isIE = document.body.style.msTouchAction !== undefined;
            if (isIE) {
                window.setTimeout(() => {
                    focus();
                });
            }
            else {
                focus();
            }
        }
    }, [focus]);
    const handlePopupVisibleChange = useCallback((popupVisible) => {
        var _a;
        if (!('popupVisible' in props)) {
            setPopupVisible(popupVisible);
            setInputFocused(popupVisible);
            setInputValue(prevInputValue => (popupVisible ? prevInputValue : ''));
        }
        (_a = props.onPopupVisibleChange) === null || _a === void 0 ? void 0 : _a.call(props, popupVisible);
    }, [props]);
    const handleValueChange = useCallback((value, selectedOptions) => {
        !isMulti && setInputValue('');
        const selectedOptionFirst = isMulti ? {} : selectedOptions[0];
        if (selectedOptionFirst === null || selectedOptionFirst === void 0 ? void 0 : selectedOptionFirst.__IS_FILTERED_OPTION) {
            const unwrappedValue = selectedOptionFirst[keepFilteredValueField] === undefined
                ? first(value)
                : selectedOptionFirst[keepFilteredValueField];
            const unwrappedSelectedOptions = selectedOptionFirst.path;
            updateValue(unwrappedValue, unwrappedSelectedOptions);
            return;
        }
        updateValue(value, selectedOptions);
        if (isMulti) {
            window.setTimeout(() => {
                const { scrollHeight, clientHeight } = tagsRef.current;
                const maxScrollTop = scrollHeight - clientHeight;
                tagsRef.current.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
            }, 100);
        }
    }, [isMulti, updateValue]);
    const handleInputClick = useCallback((e) => {
        if (inputFocused || popupVisible) {
            e.stopPropagation();
        }
    }, [inputFocused, popupVisible]);
    const handleInputBlur = useCallback(() => setInputFocused(false), []);
    const handleInputFocus = useCallback(() => setInputFocused(true), []);
    const handleInputKeyDown = useCallback((e, firstFilteredOptionValue) => {
        if (e.keyCode === KeyCode.BACKSPACE || e.keyCode === KeyCode.SPACE) {
            e.stopPropagation();
        }
        if (e.keyCode === KeyCode.ENTER && firstFilteredOptionValue) {
            const { options } = props;
            const names = getFilledFieldNames(props);
            const newValue = isMulti ? (() => {
                if (isIncludeArray(value, firstFilteredOptionValue)) {
                    return value;
                }
                return [...value, firstFilteredOptionValue];
            })() : firstFilteredOptionValue;
            const selectedOptions = isMulti ? map(newValue, valueItem => arrayTreeFilter(options, (o, level) => o[names.value] === valueItem[level], { childrenKeyName: names.children })) : arrayTreeFilter(options, (o, level) => o[names.value] === get(firstFilteredOptionValue, level), { childrenKeyName: names.children });
            updateValue(newValue, selectedOptions);
            setInputValue('');
            handlePopupVisibleChange(false);
        }
    }, [isMulti, value, props, updateValue, handlePopupVisibleChange]);
    const handleInputChange = useCallback((e) => {
        if (!popupVisible) {
            handlePopupVisibleChange(true);
        }
        const inputValue = e.target.value;
        setInputValue(inputValue);
    }, [popupVisible, handlePopupVisibleChange]);
    const handleCompositionStart = useCallback((compositionStart) => {
        setInputCompositionStart(compositionStart);
    }, []);
    const handleSelectionItemClear = useCallback((e, valueIndex) => {
        e.preventDefault();
        e.stopPropagation();
        const newValue = value.filter(valueItem => !isEqualArrays(valueItem, value[valueIndex]));
        const { options } = props;
        const names = getFilledFieldNames(props);
        const selectedOptions = map(newValue, valueItem => arrayTreeFilter(options, (o, level) => o[names.value] === valueItem[level], { childrenKeyName: names.children }));
        updateValue(newValue, selectedOptions);
    }, [value, updateValue, props]);
    const handleSelectionClear = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!inputValue) {
            handlePopupVisibleChange(false);
            cachedRef.current.clearSelectionTimer = window.setTimeout(() => {
                updateValue([]);
            }, 200);
        }
        else {
            setInputValue('');
        }
        // 调用onClear清空函数
        onClear && onClear();
    }, [inputValue, updateValue, handlePopupVisibleChange, onClear]);
    const label = useMemo(() => {
        const { options, displayRender = defaultDisplayRender } = props;
        const names = getFilledFieldNames(props);
        if (isMulti) {
            const selectedOptions = map(value, valueItem => arrayTreeFilter(options, (o, level) => o[names.value] === valueItem[level], { childrenKeyName: names.children }));
            const label = selectedOptions.length ? selectedOptions.map(o => o.map(q => q[names.label])) : value;
            return map(label, labelItem => displayRender(labelItem, selectedOptions));
        }
        const unwrappedValue = Array.isArray(first(value)) ? first(value) : value;
        const selectedOptions = arrayTreeFilter(options, (o, level) => o[names.value] === get(unwrappedValue, level), { childrenKeyName: names.children });
        const label = selectedOptions.length ? selectedOptions.map(o => o[names.label]) : value;
        return displayRender(label, selectedOptions);
    }, [isMulti, value, props]);
    const renderCascader = useCallback((context, defaultLocale, size) => {
        var _a, _b;
        const locale = Object.assign(Object.assign({}, defaultLocale), props.locale);
        const { prefixCls: customizePrefixCls, inputPrefixCls: customizeInputPrefixCls, className, size: customizeSize, disabled = false, bordered = true, allowClear = true, showSearch = false, style, placeholder = locale.placeholder || 'Please select', expandIcon, suffixIcon, popupClassName, notFoundContent, maxTagTextLength = 8, maxTagCount, showCheckAll = true, dropdownRender, children } = props, otherProps = __rest(props, ["prefixCls", "inputPrefixCls", "className", "size", "disabled", "bordered", "allowClear", "showSearch", "style", "placeholder", "expandIcon", "suffixIcon", "popupClassName", "notFoundContent", "maxTagTextLength", "maxTagCount", "showCheckAll", "dropdownRender", "children"]);
        const { getPopupContainer: getContextPopupContainer, getPrefixCls, direction } = context;
        const isRtlLayout = direction === 'rtl';
        const mergedSize = customizeSize || size;
        const prefixCls = getPrefixCls('cascader', customizePrefixCls);
        const inputPrefixCls = getPrefixCls('input', customizeInputPrefixCls);
        const sizeCls = classnames({
            [`${inputPrefixCls}-lg`]: mergedSize === 'large',
            [`${inputPrefixCls}-sm`]: mergedSize === 'small'
        });
        const pickerCls = classnames(`${prefixCls}-picker`, {
            [`${prefixCls}-picker-multi`]: isMulti,
            [`${prefixCls}-picker-rtl`]: isRtlLayout,
            [`${prefixCls}-picker-with-input-value`]: inputValue,
            [`${prefixCls}-picker-disabled`]: disabled,
            [`${prefixCls}-picker-lg`]: mergedSize === 'large',
            [`${prefixCls}-picker-sm`]: mergedSize === 'small',
            [`${prefixCls}-picker-show-search`]: !!showSearch,
            [`${prefixCls}-picker-focused`]: inputFocused,
            [`${prefixCls}-picker-expand`]: popupVisible,
            [`${prefixCls}-picker-borderless`]: !bordered
        }, className);
        const arrowCls = classnames({
            [`${prefixCls}-picker-arrow`]: true,
            [`${prefixCls}-picker-arrow-expand`]: popupVisible
        });
        let { options } = props;
        const names = getFilledFieldNames(props);
        const isOptionsEmpty = !(options && options.length > 0);
        if (!isOptionsEmpty) {
            if (inputValue) {
                options = generateFilteredOptions(prefixCls, () => renderEmpty(locale.filterEmpty));
            }
        }
        else {
            options = [getEmptyNode(names, notFoundContent || renderEmpty(locale.empty))];
        }
        if (!popupVisible) {
            options = ((_a = cachedRef.current) === null || _a === void 0 ? void 0 : _a.options) || [];
        }
        else {
            cachedRef.current.options = options;
        }
        const expandIconNode = expandIcon || (isRtlLayout ? <OutlinedLeft /> : <OutlinedRight />);
        const loadingIcon = (<span className={`${prefixCls}-menu-item-loading-icon`}>
                <OutlinedLoading animation="spin"/>
            </span>);
        const clearIcon = (allowClear && !disabled && get(value, 'length') > 0) || inputValue ? (<Clear className={`${prefixCls}-picker-clear`} onClick={handleSelectionClear}/>) : null;
        const inputIcon = suffixIcon ? replaceElement(suffixIcon, <span className={`${prefixCls}-picker-arrow`} aria-disabled={disabled}>
                {suffixIcon}
            </span>, () => ({
            className: classnames({
                [suffixIcon.props.className]: suffixIcon.props.className,
                [`${prefixCls}-picker-arrow`]: true
            }),
            disabled
        })) : <OutlinedDown className={arrowCls} disabled={disabled}/>;
        const multiLabelContent = (() => {
            if (!isMulti) {
                return null;
            }
            let multiLabel = label;
            let extraCount = 0;
            let extraLabelsTip;
            if (typeof maxTagCount === 'number' && maxTagCount >= 0) {
                multiLabel = label.slice(0, maxTagCount);
                extraCount = label.length - maxTagCount;
                if (extraCount > 0) {
                    const extraLabels = label.slice(maxTagCount);
                    extraLabelsTip = extraLabels.map(label => (<div className={`${prefixCls}-tag-tooltip-label-item`}>{label}</div>));
                }
            }
            return (<Fragment>
                    {multiLabel.map((labelItem, labelIndex) => {
                    let displayLabel = labelItem;
                    const isPure = typeof maxTagTextLength === 'number' && isPureType(labelItem);
                    if (isPure) {
                        const strLabel = String(displayLabel);
                        if (strLabel.length > maxTagTextLength) {
                            displayLabel = `${strLabel.slice(0, maxTagTextLength)}...`;
                        }
                    }
                    const tagCls = classnames(`${prefixCls}-tag`, { [`${prefixCls}-tag-mixed`]: !isPure });
                    const currentItemValue = value[labelIndex];
                    const closable = !isIncludeArray(disabledValue, currentItemValue);
                    return (<div className={`${prefixCls}-tag-wrap`} key={labelIndex}>
                                <Tooltip title={labelItem} overlayClassName={`${prefixCls}-tag-tooltip`}>
                                    <Tag className={tagCls} disabled={disabled} closable={closable} onClose={e => !disabled && handleSelectionItemClear(e, labelIndex)}>{displayLabel}</Tag>
                                </Tooltip>
                            </div>);
                })}
                    {extraCount > 0 && (<Tooltip title={extraLabelsTip} overlayClassName={`${prefixCls}-tag-tooltip`}>
                            <div className={`${prefixCls}-tag-wrap`} key="extra">
                                <Tag className={`${prefixCls}-tag`}>+ {extraCount} ...</Tag>
                            </div>
                        </Tooltip>)}
                </Fragment>);
        })();
        const getPopupContainer = props.getPopupContainer || getContextPopupContainer;
        const rcCascaderPopupClassName = classnames(popupClassName, {
            [`${prefixCls}-menu-${direction}`]: direction === 'rtl',
            [`${prefixCls}-menu-search`]: showSearch && inputValue,
            [`${prefixCls}-menu-empty`]: options.length === 1 && (options[0][names.value] === 'ACUD_CASCADER_NOT_FOUND'
                || React.isValidElement(options[0][names.value]))
        });
        const dropdownMenuColumnStyle = {};
        const isNotFound = (options || []).length === 1 && options[0].isEmptyNode;
        if (isNotFound) {
            dropdownMenuColumnStyle.height = 'auto';
        }
        const resultListMatchInputWidth = showSearch.matchInputWidth !== false;
        if (resultListMatchInputWidth && (inputValue || isNotFound) && pickerRef.current) {
            const pickerWidth = (_b = pickerRef.current) === null || _b === void 0 ? void 0 : _b.offsetWidth;
            dropdownMenuColumnStyle.width = pickerWidth;
            dropdownMenuColumnStyle.maxWidth = pickerWidth;
        }
        const inputProps = omit(
        // Not know why these props left
        otherProps, [
            'onChange',
            'onClear',
            'options',
            'popupPlacement',
            'transitionName',
            'displayRender',
            'onPopupVisibleChange',
            'onDropdownMenuScroll',
            'changeOnSelect',
            'expandTrigger',
            'popupVisible',
            'getPopupContainer',
            'loadData',
            'filterOption',
            'renderFilteredOption',
            'sortFilteredOption',
            'fieldNames',
            'mode',
            'menusEdgeBuffer',
            'dropdownMenuRender',
            'virtual'
        ]);
        const firstFilteredOptionValue = (() => {
            const firstOption = first(options);
            const { __IS_FILTERED_OPTION: isFilteredOption, disabled, value } = firstOption || {};
            if (isFilteredOption && !disabled) {
                return value;
            }
        })();
        const customInput = isMulti ? (<div ref={pickerRef} style={style} className={pickerCls} aria-disabled={disabled} onClick={showSearch ? handleClick : undefined} onMouseDown={showSearch ? handleMouseDown : undefined}>
                <div className={`${prefixCls}-tags ${sizeCls}`} ref={tagsRef}>
                    {multiLabelContent}
                    <div className={`${prefixCls}-input-wrap`} style={{ width: value && value.length ? inputWidth : '100%' }} onClick={showSearch ? focus : undefined} onFocus={showSearch ? focus : undefined} onBlur={showSearch ? blur : undefined}>
                        <Input {...inputProps} tabIndex={-1} ref={inputRef} prefixCls={inputPrefixCls} placeholder={value && value.length > 0 ? '' : placeholder} className={`${prefixCls}-input ${sizeCls}`} value={inputValue} disabled={disabled} readOnly={!showSearch} autoComplete={inputProps.autoComplete || 'off'} onClick={showSearch ? handleInputClick : undefined} onBlur={showSearch ? handleInputBlur : undefined} onFocus={showSearch ? handleInputFocus : undefined} onKeyDown={e => handleInputKeyDown(e, firstFilteredOptionValue)} onChange={showSearch ? handleInputChange : undefined}/>
                        {/* Measure Node */}
                        <span ref={measureRef} className={`${prefixCls}-mirror`} aria-hidden>
                            {inputValue}&nbsp;
                        </span>
                    </div>
                </div>
                {clearIcon}
                {inputIcon}
            </div>) : (<span ref={pickerRef} style={style} className={pickerCls} aria-disabled={disabled} onClick={showSearch ? handleClick : undefined}>
                {label && !inputValue && !inputCompositionStart && (<Tooltip title={pickerLabelOverflow ? label : undefined} overlayClassName={`${prefixCls}-picker-label-tooltip`}>
                        <span ref={pickerLabelRef} className={`${prefixCls}-picker-label`} aria-disabled={disabled} onMouseOver={handlePickerLabelHover}>
                            {label}
                        </span>
                    </Tooltip>)}
                <Input {...inputProps} tabIndex={-1} ref={inputRef} prefixCls={inputPrefixCls} placeholder={value && value.length > 0 ? '' : placeholder} className={`${prefixCls}-input ${sizeCls}`} value={inputValue} disabled={disabled} readOnly={!showSearch} autoComplete={inputProps.autoComplete || 'off'} onClick={showSearch ? handleInputClick : undefined} onBlur={showSearch ? handleInputBlur : undefined} onKeyDown={e => handleInputKeyDown(e, firstFilteredOptionValue)} onChange={showSearch ? handleInputChange : undefined} onCompositionStart={showSearch ? () => handleCompositionStart(true) : undefined} onCompositionEnd={showSearch ? () => handleCompositionStart(false) : undefined}/>
                {clearIcon}
                {inputIcon}
            </span>);
        const input = children || customInput;
        const rcCascaderProps = omit(props, [
            'inputIcon',
            'expandIcon',
            'loadingIcon',
            'bordered',
            'className'
        ]);
        const rootPrefixCls = getPrefixCls();
        const firstOptionValue = get(props, 'options[0].value');
        const initialActiveValue = firstOptionValue !== undefined ? [firstOptionValue] : undefined;
        return (<RcCascader {...rcCascaderProps} value={value} initialActiveValue={initialActiveValue} options={options} originOptions={props.options} keyword={inputValue} prefixCls={prefixCls} expandIcon={expandIconNode} loadingIcon={loadingIcon} size={mergedSize} withCheckAll={inputValue ? false : showCheckAll} locale={locale} getPopupContainer={getPopupContainer} popupVisible={popupVisible} popupClassName={rcCascaderPopupClassName} popupPlacement={getPopupPlacement(direction)} dropdownMenuColumnStyle={dropdownMenuColumnStyle} dropdownRender={dropdownRender} transitionName={getTransitionName(rootPrefixCls, 'slide-up', props.transitionName)} onPopupVisibleChange={handlePopupVisibleChange} onChange={handleValueChange}>
                {input}
            </RcCascader>);
    }, [
        props,
        isMulti,
        label,
        value,
        disabledValue,
        pickerLabelOverflow,
        inputValue,
        inputWidth,
        inputFocused,
        inputCompositionStart,
        popupVisible,
        blur,
        focus,
        getPopupPlacement,
        generateFilteredOptions,
        handleClick,
        handleMouseDown,
        handlePickerLabelHover,
        handleInputClick,
        handleInputBlur,
        handleInputFocus,
        handleInputKeyDown,
        handleInputChange,
        handleCompositionStart,
        handleSelectionClear,
        handleSelectionItemClear,
        handleValueChange,
        handlePopupVisibleChange
    ]);
    useImperativeHandle(ref, () => ({
        focus,
        blur
    }));
    useLayoutEffect(() => {
        measureRef.current && setInputWidth(measureRef.current.scrollWidth);
    }, [inputValue]);
    useEffect(() => {
        var _a;
        let clearSelectionTimer = (_a = cachedRef.current) === null || _a === void 0 ? void 0 : _a.clearSelectionTimer;
        return () => {
            if (clearSelectionTimer) {
                clearTimeout(clearSelectionTimer);
            }
        };
    }, []);
    useEffect(() => {
        setValue(prevValue => ('value' in props && !isEqual(prevValue, props.value) ? props.value : prevValue));
    }, [props.value]);
    useEffect(() => {
        if ('popupVisible' in props) {
            setPopupVisible(props.popupVisible);
        }
    }, [props.popupVisible]);
    return (<ConfigConsumer>
            {context => (<LocaleReceiver componentName="Cascader">
                    {locale => (<SizeContext.Consumer>
                            {size => renderCascader(context, locale, size)}
                        </SizeContext.Consumer>)}
                </LocaleReceiver>)}
        </ConfigConsumer>);
};
export default forwardRef(Cascader);
